import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Table } from "@tanstack/react-table";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import { syncClientsClientsSyncPost, SyncClientsResponse } from "@/client-admin";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DataTableViewOptions } from "@/components/admin/UserTable/data-table-view-options";
import { columns } from "./columns";
import { CreateClientDialog } from "./CreateClientDialog";

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
}

export function DataTableToolbar({ table }: DataTableToolbarProps<typeof columns>) {
  const [open, setOpen] = useState(false);
  const [syncClientResponse, setSyncClientResponse] = useState<SyncClientsResponse | null>(null);
  const { mutateAsync: syncClients, isPending: isSyncingClients } = useMutation({
    mutationFn: () => syncClientsClientsSyncPost(),
  });
  const queryClient = useQueryClient();
  return (
    <div className="grid grid-cols-1 gap-2">
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          placeholder="Filter client by client code..."
          value={(table.getColumn("client_code")?.getFilterValue() as string) ?? ""}
          onChange={(event) => table.getColumn("client_code")?.setFilterValue(event.target.value)}
        />
        <Input
          placeholder="Filter client by client name..."
          value={(table.getColumn("client_name")?.getFilterValue() as string) ?? ""}
          onChange={(event) => table.getColumn("client_name")?.setFilterValue(event.target.value)}
        />
        <CreateClientDialog />
        <Button
          onClick={async () => {
            const data = await syncClients();
            setSyncClientResponse(data);
            setOpen(true);
            queryClient.invalidateQueries({ queryKey: ["clients"] });
          }}
          variant="secondary"
        >
          {isSyncingClients ? "Loading..." : "Load new clients from Office DB"}
        </Button>
        <DataTableViewOptions table={table} />
      </div>
      <div className="flex gap-2 overflow-scroll">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-h-[90vh] w-full overflow-y-auto sm:max-w-[800px]">
            <DialogHeader>
              <DialogTitle>Sync Clients Result</DialogTitle>
              <DialogDescription>
                Review the changes made during synchronization. Created clients are new clients that were added to the
                portal from the office database. Updated clients are clients that were already in the portal but had
                their names updated in the office database.
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="max-h-[90vh] w-full">
              <div className="flex flex-col gap-2 p-2">
                <h3 className="text-lg font-semibold">
                  Created Clients ({syncClientResponse?.created_clients.length})
                </h3>
                <pre>{JSON.stringify(syncClientResponse?.created_clients, null, 2)}</pre>
                <h3 className="text-lg font-semibold">
                  Updated Clients ({syncClientResponse?.updated_clients.length})
                </h3>
                <pre>{JSON.stringify(syncClientResponse?.updated_clients, null, 2)}</pre>
              </div>
              <DialogFooter className="flex justify-end p-2">
                <Button className="w-full p-2" variant="outline" onClick={() => setOpen(false)}>
                  Close
                </Button>
              </DialogFooter>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
